import React, { useCallback, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Link, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Activity,
  Award,
  BarChart3,
  Bell,
  Bot,
  ChevronRight,
  ClipboardCheck,
  Download,
  Eye,
  GitBranch,
  GitFork,
  HeartPulse,
  Home,
  LockKeyhole,
  Loader2,
  Network,
  Power,
  RefreshCw,
  Settings,
  ShieldCheck,
  Target,
  UsersRound,
  UserPlus,
} from "lucide-react";
import { ControlledErrorPanel, SetupStateBadge } from "./components/production/StatusBadges";
import { IntegrationHealthCenter } from "./components/production/IntegrationHealthCenter";
import { ApiModulePage, DigitalTwinPage, VoiceCommandPage } from "./components/production/AdvancedModules";
import * as ProjectRoutes from "./components/production/ProjectRoutes";
import * as ReportRoutes from "./components/production/ReportsRoutes";
import * as AnalyticsRoutes from "./components/production/AnalyticsRoutes";
import * as TeamRoutes from "./components/production/TeamRoutes";
import * as WorkflowRoutes from "./components/production/WorkflowRoutes";
import * as SettingsRoutes from "./components/production/SettingsRoutes";
import * as AgentRoutes from "./components/production/AgentRoutes";
import * as IntegrationRoutes from "./components/production/IntegrationRoutes";
import * as CodeRoutes from "./components/production/CodeRoutes";
import * as HomeRoutes from "./components/production/HomeRoutes";
import * as PlanningRoutes from "./components/production/PlanningRoutes";
import * as OperationsRoutes from "./components/production/OperationsRoutes";
import * as DesignationRoutes from "./components/production/DesignationRoutes";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_OVERVIEW = {
  metrics: {},
  projects: [],
  team: [],
  risks: [],
  integrations: [],
  agent_events: [],
  reports: [],
  approvals: [],
  code_graph: [],
  recommendations: [],
};

const navItems = [
  { path: "/", label: "Home", icon: Home },
  { path: "/projects", label: "Projects", icon: ClipboardCheck },
  { path: "/planning", label: "Planning", icon: Target },
  { path: "/workflow", label: "Workflow", icon: GitFork },
  { path: "/reports", label: "Reports", icon: Download },
  { path: "/code", label: "Code Graph", icon: GitBranch },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/agents", label: "AI Agents", icon: Bot },
  { path: "/team", label: "Team", icon: UsersRound },
  { path: "/designations", label: "Designations", icon: Award },
  { path: "/integrations", label: "Integrations", icon: Network },
  { path: "/integration-health", label: "Health Center", icon: Activity },
  { path: "/audit-log", label: "Audit Log", icon: Eye },
  { path: "/access", label: "Settings", icon: Settings },
];

const pageTitles = {
  "/": "AI Powered Analytics and Intelligence",
  "/projects": "Projects",
  "/analytics": "Analytics",
  "/agents": "AI Agents",
  "/team": "Team",
  "/designations": "Designations",
  "/integrations": "Integrations",
  "/integration-health": "Integration Health Center",
  "/access": "Settings",
  "/access/notifications": "Settings",
  "/access/security": "Settings",
  "/team/detail": "Team Detail",
  "/team/members": "Team Members",
  "/team/members/profile": "Member Profile",
  "/agents/ask": "AI Agents",
  "/agents/activity": "AI Agents",
  "/analytics/predictive": "Predictive Analysis",
  "/analytics/emotional": "Emotional Analysis",
  "/analytics/hyper": "Hyper Personalization",
  "/workflow": "Workflow",
  "/reports": "Reports",
  "/planning": "Planning",
  "/code": "Code Graph",
  "/audit-log": "Audit Log",
  "/innovation": "Innovation Engine",
  "/iot": "IoT Monitoring",
  "/digital-twin": "Digital Twin Simulation",
  "/voice": "Voice Project Management",
  "/collaboration": "Cross-company Collaboration",
  "/rerun": "Selective Re-run",
  "/orchestration": "AI Orchestration",
  "/tester": "Tester Agent Workflow",
};

const sampleProjects = [
  { id: "education-app", name: "Educational App", code: "#U30125", status: "In Progress", progress: 15, risk: "Low", timeline: "29 Dec–12 Feb", alerts: "01", color: "blue" },
  { id: "api-integration", name: "API Integration", code: "#U30125", status: "To be started", progress: 0, risk: "--", timeline: "29 Dec–12 Feb", alerts: "01", color: "orange" },
  { id: "learning-core", name: "Educational App", code: "#U30125", status: "Completed", progress: 100, risk: "--", timeline: "29 Dec–12 Feb", alerts: "01", color: "green" },
  { id: "agent-api", name: "API Integration", code: "#U30125", status: "On hold", progress: 50, risk: "Critical", timeline: "29 Dec–12 Feb", alerts: "01", color: "yellow" },
  { id: "school-portal", name: "Educational App", code: "#U30125", status: "In Progress", progress: 15, risk: "Medium", timeline: "29 Dec–12 Feb", alerts: "01", color: "blue" },
  { id: "sync-api", name: "API Integration", code: "#U30125", status: "To be started", progress: 0, risk: "--", timeline: "29 Dec–12 Feb", alerts: "01", color: "orange" },
  { id: "student-app", name: "Educational App", code: "#U30125", status: "In Progress", progress: 15, risk: "Low", timeline: "29 Dec–12 Feb", alerts: "01", color: "yellow" },
];

const taskCards = [
  { id: "t30125-a", title: "Configuration to enable/disable features", code: "#T30125", priority: "high", assignee: "Akash Menon", status: "Testing", type: "Frontend", bug: "Bug" },
  { id: "t30125-b", title: "Configuration to enable/disable features", code: "#T30125", priority: "high", assignee: "Akash Menon", status: "Testing", type: "Frontend", bug: "Bug" },
  { id: "b30125-a", title: "Fixing toggle button", code: "#B30125", priority: "medium", assignee: "Akash Menon", status: "Testing", type: "Frontend", bug: "Bug" },
];

const signalData = [
  { name: "Mon", delay: 22, morale: 78, workload: 72 },
  { name: "Tue", delay: 26, morale: 75, workload: 78 },
  { name: "Wed", delay: 35, morale: 68, workload: 86 },
  { name: "Thu", delay: 31, morale: 74, workload: 81 },
  { name: "Fri", delay: 28, morale: 82, workload: 76 },
  { name: "Now", delay: 31, morale: 76, workload: 77 },
];

const demoRoles = [
  { role: "admin", label: "Admin", detail: "Manage connectors, users, approvals, exports" },
  { role: "project_manager", label: "Project Manager", detail: "Plan projects, approve changes, export packs" },
  { role: "developer", label: "Developer", detail: "Use agents and inspect code graph" },
  { role: "tester", label: "Tester", detail: "Use QA planning and test recommendations" },
  { role: "executive", label: "Executive", detail: "Read portfolio insights and export packs" },
];

const setupRequiredStatuses = ["not configured", "demo", "mock", "setup", "browser voice ready"];

function connectorNeedsSetup(status = "") {
  return setupRequiredStatuses.some((flag) => status.toLowerCase().includes(flag));
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, errorMessage: error?.message || "Unexpected interface error" };
  }

  componentDidCatch(error, info) {
    console.error("APMS UI boundary caught error", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="production-error" data-testid="production-error-boundary">
          <ControlledErrorPanel
            title="APMS recovered from a screen issue."
            message={this.state.errorMessage}
            onRetry={() => window.location.reload()}
            testId="production-error"
          />
        </main>
      );
    }
    return this.props.children;
  }
}

function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function useAuth() {
  const [token, setToken] = useState(() => localStorage.getItem("apms_token") || "");
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("apms_user") || "null");
    } catch (err) {
      return null;
    }
  });
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const saveSession = (payload) => {
    localStorage.setItem("apms_token", payload.token);
    localStorage.setItem("apms_user", JSON.stringify(payload.user));
    setToken(payload.token);
    setUser(payload.user);
    setAuthError("");
  };

  const login = async (email, password) => {
    try {
      setAuthLoading(true);
      const response = await axios.post(`${API}/auth/login`, { email, password });
      saveSession(response.data);
    } catch (err) {
      setAuthError("Login failed. Use a demo role or valid APMS credentials.");
    } finally {
      setAuthLoading(false);
    }
  };

  const demoLogin = async (role) => {
    try {
      setAuthLoading(true);
      const response = await axios.post(`${API}/auth/demo-login`, { role });
      saveSession(response.data);
    } catch (err) {
      setAuthError("Demo role could not be started.");
    } finally {
      setAuthLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("apms_token");
    localStorage.removeItem("apms_user");
    setToken("");
    setUser(null);
  };

  return { user, token, login, demoLogin, logout, authError, authLoading };
}

function AuthGate({ auth }) {
  const [email, setEmail] = useState("admin@apms.ai");
  const [password, setPassword] = useState("admin123");
  const [signupMode, setSignupMode] = useState(false);
  const [authNotice, setAuthNotice] = useState("");
  return (
    <main className="auth-page" data-testid="auth-page">
      <section className="auth-hero" data-testid="auth-hero">
        <span className="auth-logo" data-testid="auth-brand-mark">APMS</span>
        <span className="section-kicker" data-testid="auth-kicker"><LockKeyhole size={14} /> AI Powered Analytics and Intelligence</span>
        <h1 data-testid="auth-title">Welcome to APMS</h1>
        <p data-testid="auth-description">Plan projects, coordinate AI agents, monitor morale, and manage live integrations from one secure command center.</p>
      </section>
      <section className="auth-panel" data-testid="auth-login-panel">
        <div className="auth-switch" data-testid="auth-mode-switch"><button className={!signupMode ? "active" : ""} onClick={() => setSignupMode(false)} data-testid="show-login-button">Login</button><button className={signupMode ? "active" : ""} onClick={() => setSignupMode(true)} data-testid="show-signup-button">Sign Up</button></div>
        <h2 data-testid="auth-login-title">{signupMode ? "Create your account" : "Login to your account"}</h2>
        {signupMode && <input className="text-input" defaultValue="Morgan Admin" data-testid="signup-name-input" aria-label="Name" />}
        <input className="text-input" value={email} onChange={(event) => setEmail(event.target.value)} data-testid="login-email-input" aria-label="Email" />
        <input className="text-input" type="password" value={password} onChange={(event) => setPassword(event.target.value)} data-testid="login-password-input" aria-label="Password" />
        {signupMode && <input className="text-input" type="password" defaultValue="admin123" data-testid="signup-confirm-password-input" aria-label="Confirm password" />}
        <button className="primary-button" onClick={() => auth.login(email, password)} disabled={auth.authLoading} data-testid="login-submit-button">
          {auth.authLoading ? <Loader2 className="spin" size={18} /> : <ShieldCheck size={18} />} {signupMode ? "Create demo account" : "Login"}
        </button>
        <button className="forgot-link" onClick={() => setAuthNotice("Password reset instructions are ready for the selected demo email.")} data-testid="forgot-password-link">Forgot Password?</button>
        {authNotice && <p className="notice" data-testid="auth-notice-message">{authNotice}</p>}
        {auth.authError && <p className="notice" data-testid="auth-error-message">{auth.authError}</p>}
        <div className="demo-role-grid" data-testid="demo-role-grid">
          {demoRoles.map((item) => (
            <button className="role-demo-button" key={item.role} onClick={() => auth.demoLogin(item.role)} data-testid={`demo-login-${item.role}-button`}>
              <strong data-testid={`demo-role-label-${item.role}`}>{item.label}</strong>
              <span data-testid={`demo-role-detail-${item.role}`}>{item.detail}</span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
}

function useOverview() {
  const [overview, setOverview] = useState(EMPTY_OVERVIEW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/overview`);
      setOverview(response.data);
      setError("");
    } catch (err) {
      setError("APMS data stream is temporarily unavailable.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return { overview, loading, error, refresh };
}

function Shell({ overview, loading, error, refresh, auth }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [actionToast, setActionToast] = useState("");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const activeItem = navItems.find((item) => item.path === location.pathname || (item.path !== "/" && location.pathname.startsWith(item.path))) || navItems[0];
  const pageTitle = location.pathname.startsWith("/projects/") ? "Education App [#U30125]" : pageTitles[location.pathname] || activeItem.label;

  const showAction = (message) => {
    setActionToast(message);
    window.clearTimeout(window.__apmsActionTimer);
    window.__apmsActionTimer = window.setTimeout(() => setActionToast(""), 2400);
  };

  const handleActionClick = (event) => {
    const target = event.target.closest("button, a");
    if (!target) return;
    const testId = target.getAttribute("data-testid") || "";
    const label = (target.innerText || target.getAttribute("aria-label") || "Action").trim();
    if (target.tagName === "A") {
      showAction(`${label || "Link"} opened`);
      return;
    }
    const ignored = ["login-submit-button", "logout-button"];
    if (ignored.includes(testId) || testId.startsWith("demo-login-") || testId.startsWith("show-")) return;
    if (testId === "page-back-button") {
      event.preventDefault();
      navigate(-1);
      showAction("Went back");
      return;
    }
    if (testId === "sidebar-collapse-button") {
      event.preventDefault();
      setSidebarCollapsed((value) => !value);
      showAction(sidebarCollapsed ? "Sidebar expanded" : "Sidebar collapsed");
      return;
    }
    if (testId === "notification-button") {
      event.preventDefault();
      navigate("/access/notifications");
      showAction("Notifications opened");
      return;
    }
    if (testId === "agent-toggle-button" || testId.includes("ask-agent")) {
      event.preventDefault();
      navigate("/agents/ask");
      showAction("Ask Agent opened");
      return;
    }
    if (testId === "create-new-project-button" || testId.includes("add-work-item") || testId.includes("add-timeline") || testId.includes("add-file") || testId.includes("add-") && testId.includes("task-button")) {
      event.preventDefault();
      navigate("/planning");
      showAction("Project planning form opened");
      return;
    }
    if (testId.includes("filter-")) {
      event.preventDefault();
      showAction(`${label} filter applied`);
      return;
    }
    if (testId.includes("approve")) {
      showAction("Recommendation approved");
      return;
    }
    if (testId.includes("reject") || testId.includes("dismiss")) {
      showAction("Recommendation dismissed");
      return;
    }
    if (testId.includes("sync")) {
      showAction("Sync check started");
      return;
    }
    if (testId.includes("download") || testId.includes("save") || testId.includes("configure") || testId.includes("compare") || testId.includes("edit") || testId.includes("file") || testId.includes("pagination") || testId.includes("prev") || testId.includes("next")) {
      showAction(`${label || "Action"} completed`);
      return;
    }
    if (testId === "forgot-password-link") {
      showAction("Password reset instructions will be sent to the selected email");
      return;
    }
    showAction(`${label || "Action"} selected`);
  };

  return (
    <div className={`apms-shell reference-shell ${sidebarCollapsed ? "sidebar-collapsed" : ""}`} data-testid="apms-shell" onClickCapture={handleActionClick}>
      <aside className="sidebar" data-testid="sidebar-navigation">
        <Link className="brand" to="/" data-testid="brand-home-link">
          <span data-testid="brand-title">APMS</span>
        </Link>
        <button className="sidebar-collapse" data-testid="sidebar-collapse-button" aria-label="Collapse sidebar"><ChevronRight size={24} /></button>
        <nav className="nav-stack" data-testid="primary-navigation">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink key={item.path} to={item.path} end={item.path === "/"} className="nav-link" data-testid={`nav-link-${item.label.toLowerCase().replaceAll(" ", "-")}`}>
                <Icon size={18} aria-hidden="true" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="sidebar-footer" data-testid="mocked-integration-disclosure">
          <span>Copyright © 2026 by APMS.</span>
          <span>All Rights Reserved.</span>
        </div>
      </aside>

      <main className="main-panel" data-testid="main-panel">
        <header className="topbar" data-testid="topbar">
          <div className="topbar-title">
            <button className="back-button" data-testid="page-back-button" aria-label="Go back"><ChevronRight size={22} /></button>
            <div>
              <h1 data-testid="active-section-heading">{pageTitle}</h1>
              {location.pathname.startsWith("/projects/") && <p className="breadcrumb" data-testid="project-breadcrumb">Projects / Education App</p>}
            </div>
          </div>
          <div className="topbar-actions">
            {error && <span className="status-pill danger" data-testid="api-error-message">{error}</span>}
            {loading ? (
              <span className="status-pill" data-testid="loading-status"><Loader2 size={14} className="spin" /> Syncing</span>
            ) : (
              <span className="status-pill success" data-testid="current-user-role"><UsersRound size={14} /> {auth.user?.role_label}</span>
            )}
            <button className="header-icon" data-testid="notification-button" aria-label="Notifications"><Bell size={24} /><span /></button>
            <button className="icon-button" onClick={refresh} data-testid="refresh-overview-button" aria-label="Refresh overview">
              <RefreshCw size={18} />
            </button>
            <button className="header-icon" onClick={auth.logout} data-testid="logout-button" aria-label="Logout"><Power size={26} /></button>
            <button className="agent-toggle" data-testid="agent-toggle-button" aria-label="AI Agent"><Bot size={27} /></button>
          </div>
        </header>
        {actionToast && <div className="action-toast" data-testid="action-feedback-toast">{actionToast}</div>}

        <Routes>
          <Route path="/" element={<HomeRoutes.HomeDashboard overview={overview} />} />
          <Route path="/projects" element={<ProjectRoutes.ProjectsPage overview={overview} />} />
          <Route path="/projects/:projectId" element={<ProjectRoutes.ProjectOverview overview={overview} />} />
          <Route path="/projects/:projectId/taskboard" element={<ProjectRoutes.ProjectTaskBoard overview={overview} />} />
          <Route path="/projects/:projectId/work-items" element={<ProjectRoutes.ProjectWorkItems />} />
          <Route path="/projects/:projectId/members" element={<ProjectRoutes.ProjectMembers />} />
          <Route path="/projects/:projectId/timeline" element={<ProjectRoutes.ProjectTimeline />} />
          <Route path="/planning" element={<PlanningRoutes.Planning overview={overview} refresh={refresh} auth={auth} />} />
          <Route path="/workflow" element={<WorkflowRoutes.WorkflowView />} />
          <Route path="/audit-log" element={<OperationsRoutes.AuditLog auth={auth} />} />
          <Route path="/innovation" element={<ApiModulePage auth={auth} title="Innovation Engine" endpoint="/innovation/opportunities" actionLabel="Refresh trend scan" />} />
          <Route path="/iot" element={<ApiModulePage auth={auth} title="IoT Integration Layer" endpoint="/iot/status" actionLabel="Refresh IoT feed" />} />
          <Route path="/digital-twin" element={<DigitalTwinPage auth={auth} />} />
          <Route path="/voice" element={<VoiceCommandPage auth={auth} />} />
          <Route path="/collaboration" element={<ApiModulePage auth={auth} title="Cross-company Agent Collaboration" endpoint="/collaboration/cross-company" actionLabel="Refresh partners" />} />
          <Route path="/rerun" element={<OperationsRoutes.SelectiveRerunPage auth={auth} />} />
          <Route path="/orchestration" element={<ApiModulePage auth={auth} title="LiteLLM + LangChain Orchestration" endpoint="/orchestration/routes" actionLabel="Refresh route map" />} />
          <Route path="/tester" element={<OperationsRoutes.TesterWorkflowPage auth={auth} />} />
          <Route path="/access" element={<SettingsRoutes.AccessControl auth={auth} />} />
          <Route path="/access/notifications" element={<SettingsRoutes.AccessControl auth={auth} mode="notifications" />} />
          <Route path="/access/security" element={<SettingsRoutes.AccessControl auth={auth} mode="security" />} />
          <Route path="/agents" element={<AgentRoutes.Agents overview={overview} />} />
          <Route path="/agents/ask" element={<AgentRoutes.Agents overview={overview} mode="ask" auth={auth} refresh={refresh} />} />
          <Route path="/agents/activity" element={<AgentRoutes.Agents overview={overview} mode="activity" />} />
          <Route path="/team" element={<TeamRoutes.TeamPage overview={overview} />} />
          <Route path="/team/detail" element={<TeamRoutes.TeamDetail overview={overview} />} />
          <Route path="/team/members" element={<TeamRoutes.TeamMembers overview={overview} />} />
          <Route path="/team/members/profile" element={<TeamRoutes.MemberProfile />} />
          <Route path="/analytics" element={<AnalyticsRoutes.Analytics overview={overview} />} />
          <Route path="/analytics/predictive" element={<AnalyticsRoutes.Analytics overview={overview} mode="predictive" />} />
          <Route path="/analytics/emotional" element={<AnalyticsRoutes.Analytics overview={overview} mode="emotional" />} />
          <Route path="/analytics/hyper" element={<AnalyticsRoutes.Analytics overview={overview} mode="hyper" />} />
          <Route path="/designations" element={<DesignationRoutes.DesignationsPage />} />
          <Route path="/integrations" element={<IntegrationRoutes.Integrations overview={overview} auth={auth} refresh={refresh} />} />
          <Route path="/integration-health" element={<IntegrationHealthCenter auth={auth} />} />
          <Route path="/reports" element={<ReportRoutes.Reports overview={overview} refresh={refresh} auth={auth} />} />
          <Route path="/code" element={<CodeRoutes.CodeGraph overview={overview} />} />
        </Routes>
      </main>
    </div>
  );
}


function App() {
  const overviewState = useOverview();
  const auth = useAuth();
  if (!auth.user) {
    return <ErrorBoundary><AuthGate auth={auth} /></ErrorBoundary>;
  }
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Shell {...overviewState} auth={auth} />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
